import getRouterBasename from '@/lib/router';
import App from 'App';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useApi,
  useAuth,
  useChatInteract,
  useConfig
} from '@chainlit/react-client';

import { MsalProvider } from '@/contexts/MsalContext';
import { setupAuthInterceptor } from '@/api';

export default function AppWrapper() {
  const [translationLoaded, setTranslationLoaded] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const { isAuthenticated, isReady } = useAuth();
  const { language: languageInUse } = useConfig();
  const { i18n } = useTranslation();
  const { windowMessage } = useChatInteract();

  // Set up auth interceptor AFTER MSAL is initialized
  useEffect(() => {
    const initAuth = async () => {
      console.log('[AppWrapper] Setting up auth interceptor...');
      try {
        const authEnabled = await setupAuthInterceptor();
        console.log('[AppWrapper] Auth interceptor configured, authEnabled =', authEnabled);
      } catch (error) {
        console.error('[AppWrapper] Failed to set up auth interceptor:', error);
      }
      // Give MSAL a moment to initialize and acquire token
      await new Promise(resolve => setTimeout(resolve, 500));
      setAuthReady(true);
      console.log('[AppWrapper] Auth interceptor ready');
    };
    initAuth();
  }, []);

  function handleChangeLanguage(languageBundle: any): void {
    i18n.addResourceBundle(languageInUse, 'translation', languageBundle);
    i18n.changeLanguage(languageInUse);
  }

  const { data: translations } = useApi<any>(
    `/project/translations?language=${languageInUse}`
  );

  useEffect(() => {
    if (!translations) return;
    handleChangeLanguage(translations.translation);
    setTranslationLoaded(true);
  }, [translations]);

  useEffect(() => {
    const handleWindowMessage = (event: MessageEvent) => {
      windowMessage(event.data);
    };
    window.addEventListener('message', handleWindowMessage);
    return () => window.removeEventListener('message', handleWindowMessage);
  }, [windowMessage]);

  // Wait for both translation and auth to be ready
  if (!translationLoaded || !authReady) return null;

  if (
    isReady &&
    !isAuthenticated &&
    window.location.pathname !== getRouterBasename() + '/login' &&
    window.location.pathname !== getRouterBasename() + '/login/callback'
  ) {
    window.location.href = getRouterBasename() + '/login';
  }
  
  return (
    <MsalProvider>
      <App />
    </MsalProvider>
  );
}
